import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref as storageRefFromPath } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Activity,
  AlertTriangle,
  BadgeDollarSign,
  Ban,
  CircleCheck,
  CircleX,
  Clock,
  Code2,
  Coins,
  Copy,
  Crown,
  ExternalLink,
  Fingerprint,
  Flag,
  History,
  ListChecks,
  MessageSquare,
  PhoneCall,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  User,
  Users,
  Wallet,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";

import CountryFlag from "../../components/common/CountryFlag";
import { auth, db, storage } from "../../firebase";
import { defaultAvatarByGender } from "../../utils/defaultAvatar";

const functions = getFunctions(undefined, "us-central1");
const nf = new Intl.NumberFormat();

function formatMaybeTimestamp(value) {
  if (value == null) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return String(value);
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    try {
      return new Date(value.seconds * 1000).toLocaleString();
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatMs(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  try {
    return new Date(n).toLocaleString();
  } catch {
    return "—";
  }
}

// adminGetUserOperationsSnapshot always normalizes timestamps to
// { ms, iso } (see functions/admin/adminGetUserOperationsSnapshot.js
// normalizeTimestamp) - never a raw Firestore Timestamp or RTDB number.
function formatOpsTimestamp(value) {
  if (value == null || typeof value !== "object" || !Number.isFinite(value.ms)) {
    return "—";
  }
  try {
    return new Date(value.ms).toLocaleString();
  } catch {
    return "—";
  }
}

function formatAgeMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const abs = Math.max(0, ms);
  if (abs < 1000) return "just now";
  const seconds = Math.floor(abs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ago`;
}

function formatBool(value, labelTrue = "Yes", labelFalse = "No") {
  if (value === true) return labelTrue;
  if (value === false) return labelFalse;
  return "—";
}

function formatLastActive(data) {
  const candidates = [
    data?.lastSeenAt,
    data?.lastSeen,
    data?.lastActiveAt,
    data?.lastOnlineAt,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const s = formatMaybeTimestamp(c);
    if (s !== "—") return s;
  }
  const ms = data?.lastSeenAtMs ?? data?.lastActiveAtMs;
  if (ms != null) return formatMs(ms);
  return formatMaybeTimestamp(data?.updatedAt);
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function jsonReplacer(key, value) {
  if (value && typeof value.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return String(value);
    }
  }
  return value;
}

function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

// Correction V3 #2 (originally added for adminForceUserOffline, now
// shared with adminAllowUserOnline too - see confirmForceOffline and
// confirmAllowOnline below): error UI must never display raw
// error.message or log the complete error object - only a fixed, safe
// message chosen from a small allowlist of known Firebase callable
// error codes (selected via t()-wrapped lookups at each call site,
// matching this file's existing i18n convention). Firebase callable
// SDKs sometimes surface the code with a leading "functions/" namespace
// prefix (e.g. "functions/permission-denied") and sometimes without it,
// depending on SDK version/transport - both forms must map identically.
function normalizeCallableErrorCode(error) {
  const raw = error && error.code != null ? String(error.code) : "";
  return raw.startsWith("functions/") ? raw.slice("functions/".length) : raw;
}

function pickPhotoRaw(user) {
  const raw = user?.photoURL || user?.profileImage || "";
  return typeof raw === "string" ? raw.trim() : "";
}

function pickCountryRaw(user) {
  const c =
    user?.displayCountry || user?.realCountry || user?.country || "";
  const s = typeof c === "string" ? c.trim() : "";
  return s;
}

function pickUsername(user) {
  const u =
    user?.username ||
    user?.userName ||
    user?.handle ||
    user?.login ||
    "";
  return typeof u === "string" ? u.trim() : "";
}

function pickLanguage(user) {
  const l =
    user?.language ||
    user?.locale ||
    user?.preferredLanguage ||
    user?.lang ||
    "";
  return typeof l === "string" ? l.trim() : "";
}

function pickEmail(user) {
  const e = user?.email ?? user?.emailAddress;
  return typeof e === "string" ? e.trim() : "";
}

function pickPhone(user) {
  const p = user?.phone ?? user?.phoneNumber ?? user?.mobile;
  return typeof p === "string" ? p.trim() : "";
}

function statOrDash(value, formatFn) {
  if (value == null || value === "") return "—";
  if (typeof formatFn === "function") return formatFn(value);
  return String(value);
}

function timestampToMs(value) {
  if (value == null) return 0;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    return Number(value.seconds) * 1000;
  }
  return 0;
}

function formatIsVirtual(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "—";
}

function mapDeviceSnapshot(snap) {
  const d = snap.data() || {};
  return {
    manufacturer: statOrDash(d.manufacturer),
    model: statOrDash(d.model),
    platform: statOrDash(d.platform),
    osVersion: statOrDash(d.osVersion),
    isVirtual: formatIsVirtual(d.isVirtual),
    firstSeenAt: formatMaybeTimestamp(d.firstSeenAt),
    lastSeenAt: formatMaybeTimestamp(d.lastSeenAt),
    lastSeenMs:
      timestampToMs(d.lastSeenAt) || timestampToMs(d.firstSeenAt),
  };
}

function DevicesUsedSection({ devices, loading, error }) {
  const hasDevices = devices.length > 0;

  return (
    <section className="admin-user-details__devices" aria-labelledby="admin-user-details-devices-title">
      <div className="admin-user-details__card admin-user-details__card--wide">
        <div className="admin-user-details__card-head">
          <span
            className="admin-user-details__card-icon admin-user-details__card-icon--devices"
            aria-hidden
          >
            <Smartphone
              className="admin-user-details__card-icon-svg"
              size={18}
              strokeWidth={2}
            />
          </span>
          <h3
            className="admin-user-details__card-title"
            id="admin-user-details-devices-title"
          >
            Devices Used
          </h3>
        </div>
        <div className="admin-user-details__card-body admin-user-details__devices-body">
          {loading ? (
            <p className="admin-user-details__devices-state muted">
              Loading devices…
            </p>
          ) : error ? (
            <p className="admin-user-details__devices-state admin-user-details__devices-state--error">
              {error}
            </p>
          ) : !hasDevices ? (
            <p className="admin-user-details__devices-state muted">
              No registered devices
            </p>
          ) : (
            <>
              <div className="admin-user-details__devices-table-wrap admin1-users__table-wrap">
                <table className="admin1-users__table admin-user-details__devices-table">
                  <thead>
                    <tr>
                      <th scope="col">Manufacturer</th>
                      <th scope="col">Model</th>
                      <th scope="col">Platform</th>
                      <th scope="col">OS</th>
                      <th scope="col">Virtual</th>
                      <th scope="col">First seen</th>
                      <th scope="col">Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device, index) => (
                      <tr key={`device-row-${index}`}>
                        <td>{device.manufacturer}</td>
                        <td>{device.model}</td>
                        <td>{device.platform}</td>
                        <td>{device.osVersion}</td>
                        <td>{device.isVirtual}</td>
                        <td>{device.firstSeenAt}</td>
                        <td>{device.lastSeenAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="admin-user-details__devices-cards">
                {devices.map((device, index) => (
                  <article
                    key={`device-card-${index}`}
                    className="admin-user-details__device-card"
                  >
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        Manufacturer
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.manufacturer}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        Model
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.model}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        Platform
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.platform}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        OS version
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.osVersion}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        Virtual
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.isVirtual}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        First seen
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.firstSeenAt}
                      </span>
                    </div>
                    <div className="admin-user-details__device-card-row">
                      <span className="admin-user-details__device-card-label">
                        Last seen
                      </span>
                      <span className="admin-user-details__device-card-value">
                        {device.lastSeenAt}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="admin-user-details__field">
      <span className="admin-user-details__field-label">{label}</span>
      <span className="admin-user-details__field-value">{children}</span>
    </div>
  );
}

function CopyUidButton({ uid, className, labelIdle = "Copy UID", labelDone = "Copied" }) {
  const [done, setDone] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(uid);
      setDone(true);
      window.setTimeout(() => setDone(false), 1600);
    } catch (e) {
      console.error("clipboard:", e);
    }
  }

  const cls =
    className || "admin-user-details__btn admin-user-details__btn--outline";

  return (
    <button
      type="button"
      className={`${cls} admin-user-details__btn--with-icon`.trim()}
      onClick={() => void handleCopy()}
    >
      <Copy className="admin-user-details__btn-icon" size={16} strokeWidth={2} aria-hidden />
      <span>{done ? labelDone : labelIdle}</span>
    </button>
  );
}

function UserAvatar({ raw, className, gender }) {
  const fb = defaultAvatarByGender(gender);
  const [src, setSrc] = useState(fb);

  useEffect(() => {
    let cancelled = false;
    const fallback = defaultAvatarByGender(gender);
    const v = String(raw || "").trim();
    if (!v) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    if (/^https?:\/\//i.test(v)) {
      setSrc(v);
      return () => {
        cancelled = true;
      };
    }
    let path = v;
    if (v.startsWith("gs://")) {
      const m = v.match(/^gs:\/\/[^/]+\/(.+)$/);
      path = m ? m[1] : "";
    }
    if (!path) {
      setSrc(fallback);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const url = await getDownloadURL(storageRefFromPath(storage, path));
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) setSrc(fallback);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, gender]);

  return (
    <img
      src={src}
      alt=""
      className={className || "admin-user-details__hero-avatar"}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = defaultAvatarByGender(gender);
      }}
    />
  );
}

function CardShell({ index, title, iconVariant, Icon, children }) {
  const SvgIcon = Icon;
  return (
    <article className="admin-user-details__card">
      <div className="admin-user-details__card-head">
        <span
          className={`admin-user-details__card-icon admin-user-details__card-icon--${iconVariant}`}
          aria-hidden
        >
          {SvgIcon ? (
            <SvgIcon
              className="admin-user-details__card-icon-svg"
              size={18}
              strokeWidth={2}
            />
          ) : null}
        </span>
        <h3
          className="admin-user-details__card-title"
          id={`admin-user-details-card-${index}`}
        >
          {index}. {title}
        </h3>
      </div>
      <div className="admin-user-details__card-body">{children}</div>
    </article>
  );
}

const SUSPEND_REASON_MAX = 300;

// Phase ADMIN-GRANT-COINS-UI: allowed amounts must match the backend
// allowlist exactly (functions/index.js APPROVED_COIN_AMOUNTS) - this is
// a display/UX constraint only, the backend re-validates independently.
const GRANT_REASON_MAX = 300;
const GRANT_COIN_AMOUNTS = [100, 250, 500, 1000, 2500, 5000, 7500, 10000];

// Phase ADMIN-FORCE-OFFLINE-UI: matches the backend's own REASON_MAX
// (functions/admin/adminForceUserOffline.js) - a display/UX constraint
// only, the backend re-validates independently.
const FORCE_OFFLINE_REASON_MAX = 300;

// Phase ADMIN-ALLOW-USER-ONLINE-UI: matches the backend's own
// REASON_MAX (functions/admin/adminAllowUserOnline.js) - a display/UX
// constraint only, the backend re-validates independently.
const ALLOW_ONLINE_REASON_MAX = 300;

// Same convention as src/components/Wallet/BuyCoinsPage.js's clientTxId
// generator - crypto.randomUUID with a collision-resistant fallback.
function makeClientTxId() {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {
    // fall through to the fallback below
  }
  return `admin_grant_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function AdminSuspendConfirmModal({
  targetLine,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  working,
  t,
}) {
  const taRef = useRef(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      taRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !working) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const n = reason.length;

  return (
    <div className="admin1-suspend-modal" role="presentation">
      <button
        type="button"
        className="admin1-suspend-modal__backdrop"
        aria-label={t("common.close", { defaultValue: "Close" })}
        disabled={working}
        onClick={() => {
          if (!working) onClose();
        }}
      />
      <div
        className="admin1-suspend-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin1-suspend-modal-title"
      >
        <div className="admin1-suspend-modal__header">
          <span className="admin1-suspend-modal__icon-wrap" aria-hidden>
            <AlertTriangle size={26} strokeWidth={2} />
          </span>
          <div className="admin1-suspend-modal__header-text">
            <h2 id="admin1-suspend-modal-title" className="admin1-suspend-modal__title">
              {t("admin.users.suspend_account", { defaultValue: "Suspend account" })}
            </h2>
            <p className="admin1-suspend-modal__subtitle">
              {t("admin.users.suspend_modal_subtitle", {
                defaultValue:
                  "The user will be signed out and blocked from the app until unsuspended.",
              })}
            </p>
          </div>
        </div>
        <p className="admin1-suspend-modal__target">{targetLine}</p>
        <label className="admin1-suspend-modal__label" htmlFor="admin1-suspend-reason-details">
          {t("admin.users.suspend_reason_label", {
            defaultValue: "Reason (optional)",
          })}
        </label>
        <textarea
          id="admin1-suspend-reason-details"
          ref={taRef}
          className="admin1-suspend-modal__textarea"
          rows={4}
          maxLength={SUSPEND_REASON_MAX}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value.slice(0, SUSPEND_REASON_MAX))}
          placeholder={t("admin.users.suspend_reason_placeholder", {
            defaultValue: "Add context for the audit log…",
          })}
          disabled={working}
          autoComplete="off"
        />
        <div className="admin1-suspend-modal__counter" aria-live="polite">
          {n} / {SUSPEND_REASON_MAX}
        </div>
        <div className="admin1-suspend-modal__footer">
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--cancel"
            disabled={working}
            onClick={() => {
              if (!working) onClose();
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--danger"
            disabled={working}
            onClick={() => void onConfirm()}
          >
            {working
              ? "…"
              : t("admin.users.suspend_confirm", { defaultValue: "Suspend account" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminGrantCoinsConfirmModal({
  targetLine,
  amountLabel,
  reason,
  onClose,
  onConfirm,
  working,
  t,
}) {
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      confirmBtnRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !working) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="admin1-suspend-modal" role="presentation">
      <button
        type="button"
        className="admin1-suspend-modal__backdrop"
        aria-label={t("common.close", { defaultValue: "Close" })}
        disabled={working}
        onClick={() => {
          if (!working) onClose();
        }}
      />
      <div
        className="admin1-suspend-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin1-grant-coins-modal-title"
      >
        <div className="admin1-suspend-modal__header">
          <span
            className="admin1-suspend-modal__icon-wrap admin1-suspend-modal__icon-wrap--confirm"
            aria-hidden
          >
            <Coins size={26} strokeWidth={2} />
          </span>
          <div className="admin1-suspend-modal__header-text">
            <h2 id="admin1-grant-coins-modal-title" className="admin1-suspend-modal__title">
              {t("admin.users.grant_coins_confirm_title", {
                defaultValue: "Confirm coin grant",
              })}
            </h2>
            <p className="admin1-suspend-modal__subtitle">
              {t("admin.users.grant_coins_confirm_subtitle", {
                defaultValue: "This adds coins to the user's balance immediately.",
              })}
            </p>
          </div>
        </div>
        <p className="admin1-suspend-modal__target">{targetLine}</p>
        <p className="admin1-suspend-modal__target">{amountLabel}</p>
        {reason ? <p className="admin1-suspend-modal__target">{reason}</p> : null}
        <div className="admin1-suspend-modal__footer">
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--cancel"
            disabled={working}
            onClick={() => {
              if (!working) onClose();
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            ref={confirmBtnRef}
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--confirm"
            disabled={working}
            onClick={() => void onConfirm()}
          >
            {working
              ? "…"
              : t("admin.users.grant_coins_confirm_cta", { defaultValue: "Grant coins" })}
          </button>
        </div>
      </div>
    </div>
  );
}

// Phase ADMIN-FORCE-OFFLINE-UI: connects the Quick Actions "Force
// offline" button to the merged/deployed adminForceUserOffline
// callable (functions/admin/adminForceUserOffline.js). Mirrors
// AdminSuspendConfirmModal/AdminGrantCoinsConfirmModal's shared
// admin1-suspend-modal chrome, using a new amber/warning variant
// (admin1-suspend-modal__btn--warning) so it reads as distinct from
// the destructive red Suspend action. This dialog only ever calls the
// secured callable - it never writes to Firestore/RTDB directly, never
// touches inCall/activeSessionId/activeSessionAt/call locks, and never
// signs the user out or ends a call (the callable itself resets
// application presence only - see the modal's own explanatory text).
function AdminForceOfflineConfirmModal({
  targetLine,
  reason,
  onReasonChange,
  validationError,
  operationError,
  onClose,
  onConfirm,
  working,
  t,
}) {
  const taRef = useRef(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      taRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !working) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const n = reason.length;

  return (
    <div className="admin1-suspend-modal" role="presentation">
      <button
        type="button"
        className="admin1-suspend-modal__backdrop"
        aria-label={t("common.close", { defaultValue: "Close" })}
        disabled={working}
        onClick={() => {
          if (!working) onClose();
        }}
      />
      <div
        className="admin1-suspend-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin1-force-offline-modal-title"
      >
        <div className="admin1-suspend-modal__header">
          <span
            className="admin1-suspend-modal__icon-wrap admin1-suspend-modal__icon-wrap--warning"
            aria-hidden
          >
            <WifiOff size={26} strokeWidth={2} />
          </span>
          <div className="admin1-suspend-modal__header-text">
            <h2
              id="admin1-force-offline-modal-title"
              className="admin1-suspend-modal__title"
            >
              {t("admin.users.force_offline_modal_title", {
                defaultValue: "Force user offline",
              })}
            </h2>
            <p className="admin1-suspend-modal__subtitle">
              {t("admin.users.force_offline_modal_subtitle", {
                defaultValue:
                  "This updates application presence only. It does not sign the user out or end an active call.",
              })}
            </p>
          </div>
        </div>
        <p className="admin1-suspend-modal__target">{targetLine}</p>
        <label
          className="admin1-suspend-modal__label"
          htmlFor="admin1-force-offline-reason"
        >
          {t("admin.users.force_offline_reason_label", { defaultValue: "Reason" })}
        </label>
        <textarea
          id="admin1-force-offline-reason"
          ref={taRef}
          className="admin1-suspend-modal__textarea"
          rows={4}
          maxLength={FORCE_OFFLINE_REASON_MAX}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={t("admin.users.force_offline_reason_placeholder", {
            defaultValue: "Explain why this user is being forced offline…",
          })}
          disabled={working}
          autoComplete="off"
          required
          aria-required="true"
          aria-invalid={validationError ? "true" : "false"}
          aria-describedby={
            validationError || operationError
              ? "admin1-force-offline-validation"
              : undefined
          }
        />
        <div className="admin1-suspend-modal__counter" aria-live="polite">
          {n} / {FORCE_OFFLINE_REASON_MAX}
        </div>
        {/* Correction V3 #1: a single conditional block renders at most
            one error paragraph - client-side validationError takes
            precedence if somehow both were ever set (in practice the
            page keeps them mutually exclusive), guaranteeing exactly
            one Force Offline error alert can ever exist, and it is
            always a descendant of this dialog. */}
        {validationError || operationError ? (
          <p
            id="admin1-force-offline-validation"
            className="admin1-suspend-modal__validation-error"
            role="alert"
          >
            {validationError || operationError}
          </p>
        ) : null}
        <div className="admin1-suspend-modal__footer">
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--cancel"
            disabled={working}
            onClick={() => {
              if (!working) onClose();
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--warning"
            disabled={working}
            onClick={() => void onConfirm()}
          >
            {working
              ? t("admin.users.force_offline_working", {
                  defaultValue: "Forcing offline…",
                })
              : t("admin.users.force_offline_confirm", {
                  defaultValue: "Force offline",
                })}
          </button>
        </div>
      </div>
    </div>
  );
}

// Phase ADMIN-ALLOW-USER-ONLINE-UI: connects the Quick Actions "Allow
// online again" button to the merged/deployed adminAllowUserOnline
// callable (functions/admin/adminAllowUserOnline.js). Mirrors
// AdminForceOfflineConfirmModal's exact chrome/structure above, reusing
// the existing green admin1-suspend-modal__icon-wrap--confirm /
// __btn--confirm variant (already used by Grant coins) rather than
// inventing a fourth color, since this is the direct complementary
// (non-destructive, positive) counterpart to the amber Force offline
// action. This dialog only ever calls the secured callable - it never
// writes online/inApp/forceOffline directly, never touches
// Firestore/RTDB directly, and never signs the user in, revokes a
// token, or touches a call (the callable itself only clears the
// force-offline restriction - see the modal's own explanatory text;
// the user only actually appears online once their own application
// reconnects for real).
function AdminAllowOnlineConfirmModal({
  targetLine,
  reason,
  onReasonChange,
  validationError,
  operationError,
  onClose,
  onConfirm,
  working,
  t,
}) {
  const taRef = useRef(null);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      taRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !working) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const n = reason.length;

  return (
    <div className="admin1-suspend-modal" role="presentation">
      <button
        type="button"
        className="admin1-suspend-modal__backdrop"
        aria-label={t("common.close", { defaultValue: "Close" })}
        disabled={working}
        onClick={() => {
          if (!working) onClose();
        }}
      />
      <div
        className="admin1-suspend-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin1-allow-online-modal-title"
      >
        <div className="admin1-suspend-modal__header">
          <span
            className="admin1-suspend-modal__icon-wrap admin1-suspend-modal__icon-wrap--confirm"
            aria-hidden
          >
            <Wifi size={26} strokeWidth={2} />
          </span>
          <div className="admin1-suspend-modal__header-text">
            <h2
              id="admin1-allow-online-modal-title"
              className="admin1-suspend-modal__title"
            >
              {t("admin.users.allow_online_modal_title", {
                defaultValue: "Allow user online again",
              })}
            </h2>
            <p className="admin1-suspend-modal__subtitle">
              {t("admin.users.allow_online_modal_subtitle", {
                defaultValue:
                  "This removes the administrative force-offline restriction only. It does not sign the user in or mark them online immediately - they will appear online only after their own application reconnects for real. It does not revoke tokens or alter or end any call.",
              })}
            </p>
          </div>
        </div>
        <p className="admin1-suspend-modal__target">{targetLine}</p>
        <label
          className="admin1-suspend-modal__label"
          htmlFor="admin1-allow-online-reason"
        >
          {t("admin.users.allow_online_reason_label", { defaultValue: "Reason" })}
        </label>
        <textarea
          id="admin1-allow-online-reason"
          ref={taRef}
          className="admin1-suspend-modal__textarea"
          rows={4}
          maxLength={ALLOW_ONLINE_REASON_MAX}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={t("admin.users.allow_online_reason_placeholder", {
            defaultValue: "Explain why this restriction is being removed…",
          })}
          disabled={working}
          autoComplete="off"
          required
          aria-required="true"
          aria-invalid={validationError ? "true" : "false"}
          aria-describedby={
            validationError || operationError
              ? "admin1-allow-online-validation"
              : undefined
          }
        />
        <div className="admin1-suspend-modal__counter" aria-live="polite">
          {n} / {ALLOW_ONLINE_REASON_MAX}
        </div>
        {/* Same single-conditional-block pattern as
            AdminForceOfflineConfirmModal above - at most one error
            paragraph can ever exist, always a descendant of this
            dialog, never in the page-level banner behind the modal. */}
        {validationError || operationError ? (
          <p
            id="admin1-allow-online-validation"
            className="admin1-suspend-modal__validation-error"
            role="alert"
          >
            {validationError || operationError}
          </p>
        ) : null}
        <div className="admin1-suspend-modal__footer">
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--cancel"
            disabled={working}
            onClick={() => {
              if (!working) onClose();
            }}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className="admin1-suspend-modal__btn admin1-suspend-modal__btn--confirm"
            disabled={working}
            onClick={() => void onConfirm()}
          >
            {working
              ? t("admin.users.allow_online_working", {
                  defaultValue: "Allowing online…",
                })
              : t("admin.users.allow_online_confirm", {
                  defaultValue: "Allow online again",
                })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===============================================================
// Phase 1 — Admin User Operations Console (read-only diagnostics only).
// Sourced from adminGetUserOperationsSnapshot (functions/admin/
// adminGetUserOperationsSnapshot.js). Strictly no mutation controls in
// this console section itself - the sections below (Auth Identity,
// Presence Diagnostics, Active Call Diagnostics, Recent Calls,
// Progression) remain read-only diagnostics only.
//
// Phase ADMIN-FORCE-OFFLINE-UI added the "Force offline" action to the
// separate Quick Actions section above (see
// AdminForceOfflineConfirmModal), wired to the now-merged/deployed
// adminForceUserOffline callable. Terminate Call and Edit Level remain
// explicitly deferred and must not be added to this file.
// ===============================================================

const OPS_WARNING_LABELS = {
  auth_identity_unavailable: "Firebase Auth identity could not be loaded.",
  rtdb_presence_unavailable: "Realtime Database presence could not be loaded.",
  presence_mismatch_firestore_vs_rtdb:
    "Presence mismatch: Firestore and Realtime Database disagree on online state.",
  in_call_without_lock: "User is marked in-call, but no active call lock was found.",
  lock_active_without_incall_flag:
    "An active call lock exists, but the user is not marked in-call.",
  call_lock_lookup_unavailable: "Call lock lookup failed.",
  lock_without_callid: "The call lock is missing a callId.",
  active_call_lookup_unavailable: "Active call lookup failed.",
  lock_points_to_missing_call: "The call lock points to a call that no longer exists.",
  lock_points_to_non_ongoing_call: "The call lock points to a call that has already ended.",
  active_call_diagnosed_stale: "The active call appears stale (no recent heartbeat).",
  male_level_inconsistent_with_total_recharge:
    "Male level does not match the stored recharge total.",
  call_price_level_exceeds_female_level: "Call price level exceeds the female level.",
  recent_calls_unavailable: "Recent calls could not be loaded.",
  // devices_unavailable is intentionally not surfaced here - the existing
  // Devices Used section below has its own independent, already-working
  // real-time data source and error state (devicesError).
};

const OPS_AUTH_WARNING_CODES = new Set(["auth_identity_unavailable"]);
const OPS_PRESENCE_WARNING_CODES = new Set([
  "rtdb_presence_unavailable",
  "presence_mismatch_firestore_vs_rtdb",
  "in_call_without_lock",
  "lock_active_without_incall_flag",
]);
const OPS_CALL_WARNING_CODES = new Set([
  "call_lock_lookup_unavailable",
  "lock_without_callid",
  "active_call_lookup_unavailable",
  "lock_points_to_missing_call",
  "lock_points_to_non_ongoing_call",
  "active_call_diagnosed_stale",
]);
const OPS_PROGRESSION_WARNING_CODES = new Set([
  "male_level_inconsistent_with_total_recharge",
  "call_price_level_exceeds_female_level",
]);

function opsWarningLabel(code) {
  return OPS_WARNING_LABELS[code] || code;
}

function OpsWarningCards({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="admin-user-details__ops-warnings">
      {warnings.map((code) => (
        <div key={code} className="admin-user-details__ops-warning-card" role="alert">
          <AlertTriangle size={14} strokeWidth={2} aria-hidden />
          <span>{opsWarningLabel(code)}</span>
        </div>
      ))}
    </div>
  );
}

function OpsBadge({ tone, children }) {
  return (
    <span className={`admin-user-details__ops-badge admin-user-details__ops-badge--${tone}`}>
      {children}
    </span>
  );
}

function OpsSectionShell({ index, title, Icon, iconVariant, warnings, children }) {
  return (
    <article className="admin-user-details__card admin-user-details__card--wide" aria-label={title}>
      <div className="admin-user-details__card-head">
        <span
          className={`admin-user-details__card-icon admin-user-details__card-icon--${iconVariant}`}
          aria-hidden
        >
          <Icon className="admin-user-details__card-icon-svg" size={18} strokeWidth={2} />
        </span>
        <h3 className="admin-user-details__card-title">{index}. {title}</h3>
      </div>
      <div className="admin-user-details__card-body">
        {children}
        <OpsWarningCards warnings={warnings} />
      </div>
    </article>
  );
}

// Correction V2 #7: the operations console shows exactly ONE loading/error
// state for the whole snapshot (not one copy per section below) - each
// section keeps only its own subsystem OpsWarningCards.
function OpsConsoleControls({ loading, error, generatedAt, onRefresh }) {
  return (
    <div className="admin-user-details__ops-console-controls">
      <div className="admin-user-details__ops-console-status">
        {error ? (
          <p
            className="admin-user-details__devices-state admin-user-details__devices-state--error"
            role="alert"
          >
            {error}
          </p>
        ) : loading ? (
          <p className="admin-user-details__devices-state muted">Loading diagnostics…</p>
        ) : (
          <p className="admin-user-details__devices-state muted">
            Last refreshed: {formatOpsTimestamp(generatedAt)}
          </p>
        )}
      </div>
      <button
        type="button"
        className="admin-user-details__btn admin-user-details__btn--outline admin-user-details__btn--with-icon"
        disabled={loading}
        onClick={onRefresh}
      >
        <RefreshCw size={16} strokeWidth={2} aria-hidden />
        <span>{loading ? "Refreshing…" : "Refresh diagnostics"}</span>
      </button>
    </div>
  );
}

function AuthIdentitySection({ snapshot, loading, error }) {
  const identity = snapshot?.identity || null;
  const warnings = (snapshot?.warnings || []).filter((w) => OPS_AUTH_WARNING_CODES.has(w));

  return (
    <OpsSectionShell index={7} title="Auth Identity" Icon={Fingerprint} iconVariant="profile" warnings={warnings}>
      {!loading && !error && identity ? (
        <>
          <DetailRow label="Auth email (Firebase Auth)">
            {identity.authEmail || "—"}
          </DetailRow>
          <DetailRow label="Firestore email">
            {identity.firestoreEmail || "—"}
          </DetailRow>
          <DetailRow label="Auth phone (Firebase Auth)">
            {identity.authPhone || "—"}
          </DetailRow>
          <DetailRow label="Firestore phone">
            {identity.firestorePhone || "—"}
          </DetailRow>
          <DetailRow label="Providers">
            {identity.providers && identity.providers.length > 0
              ? identity.providers.join(", ")
              : "—"}
          </DetailRow>
          <DetailRow label="Email verified">{formatBool(identity.emailVerified)}</DetailRow>
          <DetailRow label="Auth account disabled">{formatBool(identity.disabled)}</DetailRow>
          <DetailRow label="Account created (Auth)">
            {identity.creationTime ? new Date(identity.creationTime).toLocaleString() : "—"}
          </DetailRow>
          <DetailRow label="Last sign-in (Auth)">
            {identity.lastSignInTime ? new Date(identity.lastSignInTime).toLocaleString() : "—"}
          </DetailRow>
        </>
      ) : null}
    </OpsSectionShell>
  );
}

function PresenceDiagnosticsSection({ snapshot, loading, error }) {
  const presence = snapshot?.presence || null;
  const warnings = (snapshot?.warnings || []).filter((w) => OPS_PRESENCE_WARNING_CODES.has(w));

  const canonicalTone =
    presence?.canonicalState === "busy"
      ? "busy"
      : presence?.canonicalState === "online"
      ? "online"
      : "offline";
  const canonicalLabel =
    presence?.canonicalState === "busy"
      ? "Busy"
      : presence?.canonicalState === "online"
      ? "Online"
      : "Offline";

  return (
    <OpsSectionShell index={8} title="Presence Diagnostics" Icon={Wifi} iconVariant="safety" warnings={warnings}>
      {!loading && !error && presence ? (
        <>
          <DetailRow label="Canonical state">
            <OpsBadge tone={canonicalTone}>{canonicalLabel}</OpsBadge>
          </DetailRow>
          <DetailRow label="Firestore: online / inApp / inCall">
            {`${formatBool(presence.firestore?.online)} / ${formatBool(presence.firestore?.inApp)} / ${formatBool(presence.firestore?.inCall)}`}
          </DetailRow>
          <DetailRow label="Firestore: lastChanged">
            {formatOpsTimestamp(presence.firestore?.lastChanged)}
          </DetailRow>
          <DetailRow label="Firestore: lastSeenAt">
            {formatOpsTimestamp(presence.firestore?.lastSeenAt)}
          </DetailRow>
          <DetailRow label="RTDB: state / online / inApp">
            {`${presence.rtdb?.state || "—"} / ${formatBool(presence.rtdb?.online)} / ${formatBool(presence.rtdb?.inApp)}`}
          </DetailRow>
          <DetailRow label="RTDB: lastChanged">
            {formatOpsTimestamp(presence.rtdb?.lastChanged)}
          </DetailRow>
          <DetailRow label="RTDB: reason">{presence.rtdb?.reason || "—"}</DetailRow>
        </>
      ) : null}
    </OpsSectionShell>
  );
}

function ActiveCallSection({ snapshot, loading, error, uid }) {
  const activeCall = snapshot?.activeCall || null;
  const warnings = (snapshot?.warnings || []).filter((w) => OPS_CALL_WARNING_CODES.has(w));

  const diagnosis = activeCall?.diagnosis || "no_active_call";
  const diagnosisTone =
    diagnosis === "active" ? "active" : diagnosis === "stale" ? "stale" : diagnosis === "inconsistent" ? "inconsistent" : "none";
  const diagnosisLabel =
    diagnosis === "active"
      ? "Active"
      : diagnosis === "stale"
      ? "Stale"
      : diagnosis === "inconsistent"
      ? "Inconsistent"
      : "None";

  const call = activeCall?.call || null;
  const counterpartUid = call
    ? call.maleUid === uid
      ? call.femaleUid
      : call.maleUid
    : null;

  return (
    <OpsSectionShell index={9} title="Active Call Diagnostics" Icon={PhoneCall} iconVariant="withdrawal" warnings={warnings}>
      {!loading && !error && activeCall ? (
        <>
          <DetailRow label="Diagnosis">
            <OpsBadge tone={diagnosisTone}>{diagnosisLabel}</OpsBadge>
          </DetailRow>
          <DetailRow label="Call ID">
            {activeCall.lockCallId ? (
              <CopyUidButton uid={activeCall.lockCallId} className="admin-user-details__btn admin-user-details__btn--outline" labelIdle="Copy call ID" />
            ) : (
              "—"
            )}
          </DetailRow>
          <DetailRow label="Counterpart">
            {counterpartUid ? (
              <Link className="admin-user-details__meta-link" to={`/admin1/user/${encodeURIComponent(counterpartUid)}`}>
                {counterpartUid}
              </Link>
            ) : (
              "—"
            )}
          </DetailRow>
          <DetailRow label="Status">{call?.status || "—"}</DetailRow>
          <DetailRow label="Heartbeat age">
            {call?.heartbeatAgeMs != null ? formatAgeMs(call.heartbeatAgeMs) : "—"}
          </DetailRow>
          <DetailRow label="Started">{call?.startedAt ? formatOpsTimestamp(call.startedAt) : "—"}</DetailRow>
          {/* Strictly no action buttons in Phase 1 - diagnostics only. */}
        </>
      ) : null}
    </OpsSectionShell>
  );
}

function RecentCallsSection({ snapshot, loading, error, uid }) {
  const recentCalls = snapshot?.recentCalls || [];
  const warnings = (snapshot?.warnings || []).filter((w) => w === "recent_calls_unavailable");

  return (
    <section className="admin-user-details__devices" aria-labelledby="admin-user-details-recent-calls-title">
      <div className="admin-user-details__card admin-user-details__card--wide">
        <div className="admin-user-details__card-head">
          <span className="admin-user-details__card-icon admin-user-details__card-icon--devices" aria-hidden>
            <History className="admin-user-details__card-icon-svg" size={18} strokeWidth={2} />
          </span>
          <h3 className="admin-user-details__card-title" id="admin-user-details-recent-calls-title">
            10. Recent Calls
          </h3>
        </div>
        <div className="admin-user-details__card-body admin-user-details__devices-body">
          {!loading && !error && recentCalls.length === 0 ? (
            <p className="admin-user-details__devices-state muted">No recent calls</p>
          ) : null}
          {!loading && !error && recentCalls.length > 0 ? (
            <div className="admin-user-details__devices-table-wrap admin1-users__table-wrap">
              <table className="admin1-users__table admin-user-details__devices-table">
                <thead>
                  <tr>
                    <th scope="col">Counterpart</th>
                    <th scope="col">Role</th>
                    <th scope="col">Status</th>
                    <th scope="col">Started</th>
                    <th scope="col">Ended</th>
                    <th scope="col">Duration (s)</th>
                    <th scope="col">Billed (min)</th>
                    <th scope="col">Ended reason</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalls.map((c) => (
                    <tr key={c.callId} aria-label={`call ${c.callId}`}>
                      <td>
                        {c.counterpartUid ? (
                          <Link
                            className="admin-user-details__meta-link"
                            to={`/admin1/user/${encodeURIComponent(c.counterpartUid)}`}
                          >
                            {c.counterpartUid}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{c.role || "—"}</td>
                      <td>{c.status || "—"}</td>
                      <td>{formatOpsTimestamp(c.startedAt)}</td>
                      <td>{formatOpsTimestamp(c.endedAt)}</td>
                      <td>{c.durationSeconds != null ? nf.format(c.durationSeconds) : "—"}</td>
                      <td>{c.billedMinutes != null ? nf.format(c.billedMinutes) : "—"}</td>
                      <td>{c.endedReason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <OpsWarningCards warnings={warnings} />
        </div>
      </div>
    </section>
  );
}

function ProgressionSection({ snapshot, loading, error }) {
  const progression = snapshot?.progression || null;
  const warnings = (snapshot?.warnings || []).filter((w) => OPS_PROGRESSION_WARNING_CODES.has(w));

  return (
    <OpsSectionShell index={11} title="Progression" Icon={TrendingUp} iconVariant="premium" warnings={warnings}>
      {!loading && !error && progression ? (
        <>
          <DetailRow label="maleLevel">{progression.maleLevel ?? "—"}</DetailRow>
          <DetailRow label="malePoints">{progression.malePoints != null ? nf.format(progression.malePoints) : "—"}</DetailRow>
          <DetailRow label="maleTotalRecharge">
            {progression.maleTotalRecharge != null ? nf.format(progression.maleTotalRecharge) : "—"}
          </DetailRow>
          <DetailRow label="femaleLevel">{progression.femaleLevel ?? "—"}</DetailRow>
          <DetailRow label="leveling.tokens">
            {progression.levelingTokens != null ? nf.format(progression.levelingTokens) : "—"}
          </DetailRow>
          <DetailRow label="leveling.activeDays">{progression.levelingActiveDays ?? "—"}</DetailRow>
          <DetailRow label="callPriceLevel">{progression.callPriceLevel ?? "—"}</DetailRow>
          {/* Strictly no editing controls in Phase 1 - diagnostics only. */}
        </>
      ) : null}
    </OpsSectionShell>
  );
}

export default function UserDetailsPage() {
  const { t } = useTranslation();
  const { uid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [blockedByMe, setBlockedByMe] = useState(false);
  const [suspendWorking, setSuspendWorking] = useState(false);
  const [suspendFeedback, setSuspendFeedback] = useState("");
  const [suspendConfirmOpen, setSuspendConfirmOpen] = useState(false);
  const [suspendReasonDraft, setSuspendReasonDraft] = useState("");
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [grantWorking, setGrantWorking] = useState(false);
  const [grantFeedback, setGrantFeedback] = useState(null);
  const [grantConfirmOpen, setGrantConfirmOpen] = useState(false);
  const grantClientTxIdRef = useRef(null);
  // Synchronous in-flight guard: grantWorking (React state) is not
  // updated in time to block a second confirmGrantCoins() invocation
  // that happens before React re-renders (e.g. two rapid clicks in the
  // same synchronous batch) - a plain ref mutates immediately and is
  // shared across those closures, so it catches what the state guard
  // alone cannot. grantWorking is kept as-is for rendering/disabled UI.
  const grantInFlightRef = useRef(false);

  // Phase ADMIN-FORCE-OFFLINE-UI.
  const [forceOfflineConfirmOpen, setForceOfflineConfirmOpen] = useState(false);
  const [forceOfflineReasonDraft, setForceOfflineReasonDraft] = useState("");
  const [forceOfflineValidationError, setForceOfflineValidationError] = useState("");
  // Correction V3 #1: a hard callable failure or malformed response is
  // rendered INSIDE the still-open dialog (this state), never in the
  // page-level banner below - forceOfflineFeedback (below) is reserved
  // for post-close success/idempotent/warning states only, so the two
  // never produce a duplicate/backdrop-obscured error at the same time.
  const [forceOfflineOperationError, setForceOfflineOperationError] = useState("");
  const [forceOfflineWorking, setForceOfflineWorking] = useState(false);
  const [forceOfflineFeedback, setForceOfflineFeedback] = useState(null);
  // Same synchronous in-flight guard convention as grantInFlightRef above -
  // forceOfflineWorking (React state) is not updated in time to block a
  // second confirmForceOffline() invocation fired before React re-renders.
  const forceOfflineInFlightRef = useRef(false);

  // Phase ADMIN-ALLOW-USER-ONLINE-UI.
  const [allowOnlineConfirmOpen, setAllowOnlineConfirmOpen] = useState(false);
  const [allowOnlineReasonDraft, setAllowOnlineReasonDraft] = useState("");
  const [allowOnlineValidationError, setAllowOnlineValidationError] = useState("");
  // Same convention as forceOfflineOperationError above: a hard
  // callable failure or malformed response is rendered INSIDE the
  // still-open dialog, never in the page-level banner - allowOnlineFeedback
  // (below) is reserved for a confirmed, validated success only.
  const [allowOnlineOperationError, setAllowOnlineOperationError] = useState("");
  const [allowOnlineWorking, setAllowOnlineWorking] = useState(false);
  const [allowOnlineFeedback, setAllowOnlineFeedback] = useState(null);
  // Same synchronous in-flight guard convention as forceOfflineInFlightRef
  // above - allowOnlineWorking (React state) is not updated in time to
  // block a second confirmAllowOnline() invocation fired before React
  // re-renders.
  const allowOnlineInFlightRef = useRef(false);

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState("");

  // Phase 1 - Admin User Operations Console (read-only diagnostics only).
  const [opsSnapshot, setOpsSnapshot] = useState(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState("");

  const adminUid = auth.currentUser?.uid;

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setError("Missing user id");
      return undefined;
    }

    const ref = doc(db, "users", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setData(null);
          setError("User not found");
          setLoading(false);
          return;
        }
        setData(snap.data());
        setError("");
        setLoading(false);
      },
      (err) => {
        console.error("UserDetailsPage snapshot error:", err);
        setError(err?.message || "Failed to load user");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    const me = auth.currentUser?.uid;
    if (!me || !uid) {
      setBlockedByMe(false);
      return undefined;
    }
    const blockRef = doc(db, "blocks", `${me}_${uid}`);
    const unsub = onSnapshot(
      blockRef,
      (snap) => setBlockedByMe(snap.exists()),
      () => setBlockedByMe(false)
    );
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setDevices([]);
      setDevicesLoading(false);
      setDevicesError("");
      return undefined;
    }

    setDevicesLoading(true);
    setDevicesError("");

    const devicesRef = collection(db, "users", uid, "devices");
    const unsub = onSnapshot(
      devicesRef,
      (snap) => {
        const rows = snap.docs
          .map(mapDeviceSnapshot)
          .sort((a, b) => b.lastSeenMs - a.lastSeenMs);
        setDevices(rows);
        setDevicesLoading(false);
        setDevicesError("");
      },
      (err) => {
        console.error("UserDetailsPage devices snapshot error:", err);
        setDevices([]);
        setDevicesLoading(false);
        setDevicesError(err?.message || "Failed to load devices");
      }
    );

    return () => unsub();
  }, [uid]);

  // Phase 1 - Admin User Operations Console (read-only diagnostics only).
  // One-shot fetch via a protected callable, not a live listener - this is
  // a point-in-time diagnostics snapshot, never a source of mutation.
  // opsRequestIdRef makes only the LATEST in-flight request able to apply
  // its result to state - covers both unmount (route change) and a manual
  // Refresh click firing while the initial load is still in flight.
  const opsRequestIdRef = useRef(0);

  const fetchOpsSnapshot = useCallback((targetUid) => {
    if (!targetUid) return undefined;
    const requestId = opsRequestIdRef.current + 1;
    opsRequestIdRef.current = requestId;
    setOpsLoading(true);
    setOpsError("");

    return (async () => {
      try {
        const fn = httpsCallable(functions, "adminGetUserOperationsSnapshot");
        const res = await fn({ targetUid });
        if (opsRequestIdRef.current === requestId) {
          setOpsSnapshot(res?.data || null);
          setOpsLoading(false);
        }
      } catch (opsErr) {
        console.error("adminGetUserOperationsSnapshot:", opsErr);
        if (opsRequestIdRef.current === requestId) {
          setOpsSnapshot(null);
          setOpsError(callableErrorMessage(opsErr));
          setOpsLoading(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!uid) {
      opsRequestIdRef.current += 1;
      setOpsSnapshot(null);
      setOpsLoading(false);
      setOpsError("");
      return undefined;
    }
    void fetchOpsSnapshot(uid);
    return undefined;
  }, [uid, fetchOpsSnapshot]);

  const fullJson = useMemo(() => {
    if (!data) return "";
    try {
      return JSON.stringify(data, jsonReplacer, 2);
    } catch {
      return "{}";
    }
  }, [data]);

  const activityOverview = useMemo(() => {
    if (!data) {
      return {
        earnings7d: "—",
        callsWeek: "—",
        messagesWeek: "—",
        onlineTime7d: "—",
        logins7d: "—",
      };
    }
    const d = data;
    const e7Raw =
      d.stats?.last7DaysEarnings ??
      d.activity?.last7DaysEarnings ??
      d.activity?.earnings7d ??
      d.earningsLast7Days;
    const calls =
      d.callsThisWeek ??
      d.activity?.callsThisWeek ??
      d.stats?.callsThisWeek;
    const msgs =
      d.messagesThisWeek ??
      d.activity?.messagesThisWeek ??
      d.stats?.messagesThisWeek;
    const onlineT =
      d.onlineTime7Days ??
      d.activity?.onlineTime7Days ??
      d.stats?.onlineMinutes7d;
    const logins =
      d.loginCount7Days ??
      d.activity?.loginCount7Days ??
      d.stats?.loginCount7d;

    const e7n = Number(e7Raw);
    return {
      earnings7d:
        e7Raw != null &&
        e7Raw !== "" &&
        Number.isFinite(e7n)
          ? nf.format(e7n)
          : "—",
      callsWeek: statOrDash(calls),
      messagesWeek: statOrDash(msgs),
      onlineTime7d: statOrDash(onlineT),
      logins7d: statOrDash(logins),
    };
  }, [data]);

  const displayName = data
    ? String(data.displayName || data.name || "").trim() || "—"
    : "—";

  const isSelf = adminUid && uid === adminUid;
  const canSuspend = adminUid && uid && !isSelf;
  // Same self-target gate as canSuspend - the backend independently
  // rejects self-targeting with failed-precondition regardless of this
  // UI gate (see confirmForceOffline's error handling below).
  const canForceOffline = Boolean(adminUid && uid && !isSelf);

  // Correction V2: the merged/deployed adminGetUserOperationsSnapshot
  // does not expose presence.firestore.forceOffline at all, so gating
  // this action on the snapshot confirming the restriction made it
  // permanently unavailable. Availability is now the same basic
  // target/self gate as canForceOffline above - authenticated admin,
  // valid target, not self - and is deliberately independent of the
  // operations snapshot's loading/error/shape/forceOffline value in
  // every case. This never infers or displays whether the user is
  // currently restricted; safety is entirely the already-merged/
  // deployed adminAllowUserOnline callable's own responsibility (it
  // independently re-authorizes the caller, rejects self-targeting,
  // and is idempotent - alreadyAllowed:true when nothing needed to
  // change, with no user write and no duplicate audit entry).
  const canAllowOnline = Boolean(adminUid && uid && !isSelf);

  function openSuspendConfirm() {
    if (!canSuspend || !uid) return;
    setSuspendReasonDraft("");
    setSuspendConfirmOpen(true);
  }

  async function confirmSuspendFromModal() {
    if (!canSuspend || !uid) return;
    const reason = suspendReasonDraft.trim().slice(0, SUSPEND_REASON_MAX);

    setSuspendWorking(true);
    setSuspendFeedback("");
    try {
      const fn = httpsCallable(functions, "adminSuspendUser");
      await fn({ targetUid: uid, reason });
    } catch (error) {
      console.error("adminSuspendUser:", error);
      setSuspendFeedback(callableErrorMessage(error));
    } finally {
      setSuspendWorking(false);
      setSuspendConfirmOpen(false);
      setSuspendReasonDraft("");
    }
  }

  async function handleUnsuspend() {
    if (!canSuspend || !uid) return;
    setSuspendWorking(true);
    setSuspendFeedback("");
    try {
      const fn = httpsCallable(functions, "adminUnsuspendUser");
      await fn({ targetUid: uid });
    } catch (error) {
      console.error("adminUnsuspendUser:", error);
      setSuspendFeedback(callableErrorMessage(error));
    } finally {
      setSuspendWorking(false);
    }
  }

  // Phase ADMIN-FORCE-OFFLINE-UI: connects the Quick Actions "Force
  // offline" action to the merged/deployed adminForceUserOffline
  // callable. Never invoked during render, initial load, dialog
  // open/close, or a failed validation - only from confirmForceOffline
  // below, after an explicit confirmed click with a valid reason.
  function openForceOfflineConfirm() {
    if (!canForceOffline || !uid || forceOfflineWorking) return;
    setForceOfflineReasonDraft("");
    setForceOfflineValidationError("");
    setForceOfflineOperationError("");
    setForceOfflineFeedback(null);
    setForceOfflineConfirmOpen(true);
  }

  function closeForceOfflineConfirm() {
    if (forceOfflineWorking) return;
    setForceOfflineConfirmOpen(false);
    setForceOfflineOperationError("");
  }

  async function confirmForceOffline() {
    // Synchronous in-flight guard first (see forceOfflineInFlightRef's
    // comment above) - blocks a second invocation even before React has
    // re-rendered the disabled confirm button.
    if (forceOfflineInFlightRef.current || !canForceOffline || !uid) return;

    const trimmed = forceOfflineReasonDraft.trim();
    if (!trimmed) {
      // Correction V3 #1: clearing any stale operation error too, so a
      // validation message and a leftover hard-error message can never
      // both be visible at once (exactly one alert at any time).
      setForceOfflineOperationError("");
      setForceOfflineValidationError(
        t("admin.users.force_offline_reason_required", {
          defaultValue: "Reason is required.",
        })
      );
      return;
    }
    if (trimmed.length > FORCE_OFFLINE_REASON_MAX) {
      setForceOfflineOperationError("");
      setForceOfflineValidationError(
        t("admin.users.force_offline_reason_too_long", {
          defaultValue: `Reason must be ${FORCE_OFFLINE_REASON_MAX} characters or fewer.`,
        })
      );
      return;
    }
    setForceOfflineValidationError("");

    setForceOfflineWorking(true);
    setForceOfflineFeedback(null);
    setForceOfflineOperationError("");
    forceOfflineInFlightRef.current = true;
    try {
      const fn = httpsCallable(functions, "adminForceUserOffline");
      const res = await fn({ targetUid: uid, reason: trimmed });

      // Correction V2 #2: the Promise resolving is not itself proof of a
      // confirmed state change - validate the response shape before
      // treating it as any kind of success. `!= null` (not a strict
      // `!==`) is deliberate: it rejects both `undefined` and `null`,
      // and `typeof null === "object"` would otherwise slip through a
      // plain typeof check.
      const rawData = res && res.data;
      const isValidResponse =
        rawData != null &&
        typeof rawData === "object" &&
        rawData.ok === true &&
        typeof rawData.targetUid === "string" &&
        rawData.targetUid === uid;

      if (!isValidResponse) {
        // Never log or display the malformed payload itself - only a
        // safe, generic message. Nothing here is treated as success:
        // the dialog stays open, the reason is preserved, and the
        // operations snapshot is not refreshed. Correction V3 #1: shown
        // inside the still-open dialog (forceOfflineOperationError),
        // never in the page-level banner behind the modal/backdrop.
        setForceOfflineOperationError(
          t("admin.users.force_offline_invalid_response", {
            defaultValue:
              "The server returned an invalid response. No successful change was confirmed.",
          })
        );
        return;
      }

      const resultData = rawData;
      const alreadyOffline = resultData.alreadyOffline === true;
      const rtdbWriteFailed = resultData.rtdbWriteFailed === true;

      if (rtdbWriteFailed) {
        // Treated as an overall success (the durable Firestore state
        // already committed) - only the RTDB mirror needs a safe retry.
        const rawWarnings = Array.isArray(resultData.warnings)
          ? resultData.warnings
          : [];
        const firstWarning = rawWarnings.find(
          (w) => typeof w === "string" && w.trim()
        );
        setForceOfflineFeedback({
          kind: "warning",
          message:
            firstWarning ||
            t("admin.users.force_offline_rtdb_warning", {
              defaultValue:
                "The Firestore presence state was updated, but the realtime status mirror could not be updated. Retry the action to repair it safely.",
            }),
        });
      } else if (alreadyOffline) {
        setForceOfflineFeedback({
          kind: "success",
          message: t("admin.users.force_offline_already_offline", {
            defaultValue:
              "User was already force-offline. The status mirror was refreshed safely.",
          }),
        });
      } else {
        setForceOfflineFeedback({
          kind: "success",
          message: t("admin.users.force_offline_success", {
            defaultValue: "User was forced offline successfully.",
          }),
        });
      }

      setForceOfflineConfirmOpen(false);
      setForceOfflineReasonDraft("");
      // Refresh the existing read-only operations snapshot via the
      // page's own established mechanism - never a full page reload,
      // never optimistically fabricated data before this point.
      void fetchOpsSnapshot(uid);
    } catch (error) {
      // Correction V3 #2: never log the complete error object (stack,
      // custom data, token/metadata may be attached to it) - only a
      // fixed label plus the normalized, allowlisted code string. Never
      // display raw error.message anywhere in the UI either.
      const normalizedCode = normalizeCallableErrorCode(error);
      console.error("adminForceUserOffline failed:", normalizedCode || "unknown");

      let safeMessage;
      if (normalizedCode === "failed-precondition") {
        safeMessage = t("admin.users.force_offline_self_target_error", {
          defaultValue: "You cannot force your own administrator account offline.",
        });
      } else if (normalizedCode === "unauthenticated") {
        safeMessage = t("admin.users.force_offline_error_unauthenticated", {
          defaultValue:
            "Your administrator session is no longer valid. Sign in again and retry.",
        });
      } else if (normalizedCode === "permission-denied") {
        safeMessage = t("admin.users.force_offline_error_permission_denied", {
          defaultValue: "You do not have permission to force this user offline.",
        });
      } else if (normalizedCode === "not-found") {
        safeMessage = t("admin.users.force_offline_error_not_found", {
          defaultValue: "The selected user no longer exists.",
        });
      } else if (normalizedCode === "invalid-argument") {
        safeMessage = t("admin.users.force_offline_error_invalid_argument", {
          defaultValue: "The request was rejected. Check the reason and try again.",
        });
      } else {
        // Covers "internal", "unknown", a missing code, network errors,
        // and any other arbitrary thrown value - never the raw code or
        // raw message.
        safeMessage = t("admin.users.force_offline_error_generic", {
          defaultValue: "Unable to force the user offline. Please try again.",
        });
      }

      // Correction V3 #1: shown inside the still-open dialog, never in
      // the page-level banner behind the modal/backdrop.
      setForceOfflineOperationError(safeMessage);
      // Modal is intentionally left open (setForceOfflineConfirmOpen is
      // never called in this branch) so the administrator can correct
      // the reason or retry without losing their draft.
    } finally {
      setForceOfflineWorking(false);
      forceOfflineInFlightRef.current = false;
    }
  }

  // Phase ADMIN-ALLOW-USER-ONLINE-UI: connects the Quick Actions "Allow
  // online again" action to the merged/deployed adminAllowUserOnline
  // callable. Never invoked during render, initial load, dialog
  // open/close, or a failed validation - only from confirmAllowOnline
  // below, after an explicit confirmed click with a valid reason.
  function openAllowOnlineConfirm() {
    if (!canAllowOnline || !uid || allowOnlineWorking) return;
    setAllowOnlineReasonDraft("");
    setAllowOnlineValidationError("");
    setAllowOnlineOperationError("");
    setAllowOnlineFeedback(null);
    setAllowOnlineConfirmOpen(true);
  }

  function closeAllowOnlineConfirm() {
    if (allowOnlineWorking) return;
    setAllowOnlineConfirmOpen(false);
    setAllowOnlineOperationError("");
  }

  async function confirmAllowOnline() {
    // Synchronous in-flight guard first (see allowOnlineInFlightRef's
    // comment above) - blocks a second invocation even before React has
    // re-rendered the disabled confirm button.
    if (allowOnlineInFlightRef.current || !canAllowOnline || !uid) return;

    const trimmed = allowOnlineReasonDraft.trim();
    if (!trimmed) {
      // Clearing any stale operation error too, so a validation message
      // and a leftover hard-error message can never both be visible at
      // once (exactly one alert at any time) - same convention as
      // confirmForceOffline above.
      setAllowOnlineOperationError("");
      setAllowOnlineValidationError(
        t("admin.users.allow_online_reason_required", {
          defaultValue: "Reason is required.",
        })
      );
      return;
    }
    if (trimmed.length > ALLOW_ONLINE_REASON_MAX) {
      setAllowOnlineOperationError("");
      setAllowOnlineValidationError(
        t("admin.users.allow_online_reason_too_long", {
          defaultValue: `Reason must be ${ALLOW_ONLINE_REASON_MAX} characters or fewer.`,
        })
      );
      return;
    }
    setAllowOnlineValidationError("");

    setAllowOnlineWorking(true);
    setAllowOnlineFeedback(null);
    setAllowOnlineOperationError("");
    allowOnlineInFlightRef.current = true;
    try {
      const fn = httpsCallable(functions, "adminAllowUserOnline");
      const res = await fn({ targetUid: uid, reason: trimmed });

      // The Promise resolving is not itself proof of a confirmed state
      // change - validate the response shape before treating it as any
      // kind of success, same discipline as confirmForceOffline above.
      // `!= null` (not a strict `!==`) rejects both `undefined` and
      // `null`, since `typeof null === "object"` would otherwise slip
      // through a plain typeof check.
      const rawData = res && res.data;
      const isValidResponse =
        rawData != null &&
        typeof rawData === "object" &&
        rawData.ok === true &&
        typeof rawData.targetUid === "string" &&
        rawData.targetUid === uid &&
        typeof rawData.alreadyAllowed === "boolean";

      if (!isValidResponse) {
        // Never log or display the malformed payload itself - only a
        // safe, generic message. Nothing here is treated as success:
        // the dialog stays open, the reason is preserved, and the
        // operations snapshot is not refreshed.
        setAllowOnlineOperationError(
          t("admin.users.allow_online_invalid_response", {
            defaultValue:
              "The server returned an invalid response. No successful change was confirmed.",
          })
        );
        return;
      }

      const alreadyAllowed = rawData.alreadyAllowed === true;
      // Wording deliberately never claims the user IS online now - only
      // that the restriction was removed / already was removed, and
      // that they will appear online after their own real reconnect.
      setAllowOnlineFeedback({
        kind: "success",
        message: alreadyAllowed
          ? t("admin.users.allow_online_already_allowed", {
              defaultValue:
                "The user was already allowed online. They will appear online after a real reconnect.",
            })
          : t("admin.users.allow_online_success", {
              defaultValue:
                "The force-offline restriction was removed. The user can appear online after their next real reconnect.",
            }),
      });

      setAllowOnlineConfirmOpen(false);
      setAllowOnlineReasonDraft("");
      // Refresh the existing read-only operations snapshot via the
      // page's own established mechanism - never a full page reload,
      // never optimistically fabricated data before this point.
      void fetchOpsSnapshot(uid);
    } catch (error) {
      // Never log the complete error object (stack, custom data,
      // token/metadata may be attached to it) - only a fixed label plus
      // the normalized, allowlisted code string. Never display raw
      // error.message anywhere in the UI either.
      const normalizedCode = normalizeCallableErrorCode(error);
      console.error("adminAllowUserOnline failed:", normalizedCode || "unknown");

      let safeMessage;
      if (normalizedCode === "failed-precondition") {
        safeMessage = t("admin.users.allow_online_self_target_error", {
          defaultValue: "You cannot target your own administrator account.",
        });
      } else if (normalizedCode === "unauthenticated") {
        safeMessage = t("admin.users.allow_online_error_unauthenticated", {
          defaultValue:
            "Your administrator session is no longer valid. Sign in again and retry.",
        });
      } else if (normalizedCode === "permission-denied") {
        safeMessage = t("admin.users.allow_online_error_permission_denied", {
          defaultValue: "You do not have permission to allow this user online.",
        });
      } else if (normalizedCode === "not-found") {
        safeMessage = t("admin.users.allow_online_error_not_found", {
          defaultValue: "The selected user no longer exists.",
        });
      } else if (normalizedCode === "invalid-argument") {
        safeMessage = t("admin.users.allow_online_error_invalid_argument", {
          defaultValue: "The request was rejected. Check the reason and try again.",
        });
      } else {
        // Covers "internal", "unknown", a missing code, network errors,
        // and any other arbitrary thrown value - never the raw code or
        // raw message.
        safeMessage = t("admin.users.allow_online_error_generic", {
          defaultValue: "Unable to allow this user online. Please try again.",
        });
      }

      // Shown inside the still-open dialog, never in the page-level
      // banner behind the modal/backdrop.
      setAllowOnlineOperationError(safeMessage);
      // Modal is intentionally left open (setAllowOnlineConfirmOpen is
      // never called in this branch) so the administrator can correct
      // the reason or retry without losing their draft.
    } finally {
      setAllowOnlineWorking(false);
      allowOnlineInFlightRef.current = false;
    }
  }

  function openGrantCoinsConfirm() {
    if (!uid || !grantAmount || grantWorking) return;
    // A fresh id is generated only when a brand-new grant attempt starts;
    // confirmGrantCoins() below reuses grantClientTxIdRef.current across
    // retries of the SAME attempt so a retry never double-grants.
    grantClientTxIdRef.current = null;
    setGrantFeedback(null);
    setGrantConfirmOpen(true);
  }

  function closeGrantCoinsConfirm() {
    if (grantWorking) return;
    setGrantConfirmOpen(false);
  }

  async function confirmGrantCoins() {
    // Guards against double-click/double-invocation even before React has
    // re-rendered the disabled button. grantInFlightRef is a plain ref
    // (synchronous, shared across closures) - unlike grantWorking (React
    // state), it is already true for a second invocation that happens
    // before the first setGrantWorking(true) has been committed/re-rendered.
    if (grantInFlightRef.current || !uid || !grantAmount) return;
    if (!grantClientTxIdRef.current) {
      grantClientTxIdRef.current = makeClientTxId();
    }

    setGrantWorking(true);
    setGrantFeedback(null);
    grantInFlightRef.current = true;
    try {
      const fn = httpsCallable(functions, "adminGrantCoins");
      const res = await fn({
        targetUid: uid,
        coins: Number(grantAmount),
        clientTxId: grantClientTxIdRef.current,
        reason: grantReason.trim().slice(0, GRANT_REASON_MAX),
      });
      const duplicated = Boolean(res?.data?.duplicated);
      // No manual refetch: users/{uid} is already a live onSnapshot
      // listener (see the effect above), so the balance shown on this
      // page updates on its own once the backend transaction commits.
      setGrantFeedback({ kind: "success", duplicated });
      setGrantConfirmOpen(false);
      setGrantAmount("");
      setGrantReason("");
      grantClientTxIdRef.current = null;
    } catch (error) {
      console.error("adminGrantCoins:", error);
      setGrantFeedback({ kind: "error", message: callableErrorMessage(error) });
      // Keep the modal open and the clientTxId stable so the admin can
      // retry the exact same attempt without risking a duplicate grant.
    } finally {
      setGrantWorking(false);
      grantInFlightRef.current = false;
    }
  }

  const verified =
    data && normalize(data.verificationStatus) === "approved";
  const isSuspended =
    data && normalize(String(data.accountStatus || "")) === "suspended";

  const usernameStr = data ? pickUsername(data) : "";
  const emailStr = data ? pickEmail(data) : "";
  const phoneStr = data ? pickPhone(data) : "";
  const langStr = data ? pickLanguage(data) : "";

  const inner = loading ? (
    <div className="admin1-state-card">Loading user…</div>
  ) : error ? (
    <div className="admin1-state-card">{error}</div>
  ) : data ? (
    <>
      <header className="admin-user-details__header">
        <div className="admin-user-details__header-top">
          <Link className="admin-user-details__back" to="/admin1/users">
            ← Back to Users
          </Link>
          <div className="admin-user-details__header-actions">
            {canSuspend ? (
              isSuspended ? (
                <button
                  type="button"
                  className="admin-user-details__btn admin-user-details__btn--success admin-user-details__btn--with-icon"
                  disabled={suspendWorking}
                  onClick={() => void handleUnsuspend()}
                >
                  <CircleCheck size={16} strokeWidth={2} aria-hidden />
                  <span>
                    {suspendWorking
                      ? "…"
                      : t("admin.users.unsuspend", { defaultValue: "Unsuspend" })}
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="admin-user-details__btn admin-user-details__btn--danger admin-user-details__btn--with-icon"
                  disabled={suspendWorking}
                  onClick={() => openSuspendConfirm()}
                >
                  <Ban size={16} strokeWidth={2} aria-hidden />
                  <span>
                    {suspendWorking
                      ? "..."
                      : t("admin.users.suspend_account", {
                          defaultValue: "Suspend account",
                        })}
                  </span>
                </button>
              )
            ) : null}
            <Link
              className="admin-user-details__btn admin-user-details__btn--secondary admin-user-details__btn--with-icon"
              to={`/user/${encodeURIComponent(uid)}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={16} strokeWidth={2} aria-hidden />
              <span>{t("admin.users.public_profile", { defaultValue: "View public profile" })}</span>
            </Link>
            <CopyUidButton uid={uid} />
          </div>
        </div>
        <div className="admin-user-details__header-text">
          <h1 className="admin-user-details__page-title">{t("admin.users.details_title", { defaultValue: "User details" })}</h1>
          <p className="admin-user-details__page-subtitle">
            {t("admin.users.details_subtitle", { defaultValue: "Detailed information and account overview" })}
          </p>
        </div>
      </header>

      {suspendFeedback ? (
        <div className="admin-user-details__feedback">{suspendFeedback}</div>
      ) : null}

      {grantFeedback ? (
        <div
          className={
            grantFeedback.kind === "success"
              ? "admin-user-details__feedback admin-user-details__feedback--success"
              : "admin-user-details__feedback"
          }
          role="status"
        >
          {grantFeedback.kind === "success"
            ? grantFeedback.duplicated
              ? t("admin.users.grant_coins_duplicate", {
                  defaultValue:
                    "This grant was already applied earlier (duplicate submission) — no additional coins were added.",
                })
              : t("admin.users.grant_coins_success", {
                  defaultValue: "Coins granted successfully.",
                })
            : grantFeedback.message}
        </div>
      ) : null}

      {forceOfflineFeedback ? (
        <div
          className={
            forceOfflineFeedback.kind === "success"
              ? "admin-user-details__feedback admin-user-details__feedback--success"
              : forceOfflineFeedback.kind === "warning"
              ? "admin-user-details__feedback admin-user-details__feedback--warning"
              : "admin-user-details__feedback"
          }
          // Correction V2 #3: only this feature's feedback banner - an
          // error is assertive (role="alert"), success/warning remain
          // polite (role="status"), matching the pre-existing
          // suspend/grant feedback conventions elsewhere on this page.
          role={forceOfflineFeedback.kind === "error" ? "alert" : "status"}
        >
          {forceOfflineFeedback.message}
        </div>
      ) : null}

      {allowOnlineFeedback ? (
        // Only ever a validated, confirmed success (see confirmAllowOnline
        // above - any hard error or malformed response is kept inside the
        // still-open dialog instead), so this banner is always the polite
        // "status" role, matching this page's success-banner convention.
        <div
          className="admin-user-details__feedback admin-user-details__feedback--success"
          role="status"
        >
          {allowOnlineFeedback.message}
        </div>
      ) : null}

      <section className="admin-user-details__hero">
        <div className="admin-user-details__hero-main">
          <UserAvatar raw={pickPhotoRaw(data)} gender={data?.gender} />
          <div className="admin-user-details__hero-content">
            <div className="admin-user-details__hero-name-row">
              <h2 className="admin-user-details__hero-name">{displayName}</h2>
              {verified ? (
                <span
                  className="admin-user-details__verified-badge"
                  title={t("admin.users.verified", { defaultValue: "Verified" })}
                >
                  <CircleCheck size={15} strokeWidth={2.5} aria-hidden />
                </span>
              ) : null}
              <div className="admin-user-details__uid-pill">
                <span className="admin-user-details__uid-pill-text">{uid}</span>
                <CopyUidButton
                  uid={uid}
                  className="admin-user-details__btn admin-user-details__btn--pill"
                  labelIdle="Copy"
                />
              </div>
            </div>
            {emailStr ? (
              <p className="admin-user-details__hero-meta">{emailStr}</p>
            ) : (
              <p className="admin-user-details__hero-meta muted">
                {t("admin.users.no_email_on_file", { defaultValue: "No email on file" })}
              </p>
            )}
            {usernameStr ? (
              <p className="admin-user-details__hero-meta mono">
                @{usernameStr}
              </p>
            ) : null}
            <div className="admin-user-details__hero-badges">
              {data.gender ? (
                <span className="admin-user-details__chip admin-user-details__chip--with-icon">
                  <Users size={15} strokeWidth={2} className="admin-user-details__chip-icon" aria-hidden />
                  {String(data.gender)}
                </span>
              ) : null}
              <span className="admin-user-details__chip admin-user-details__chip--country">
                <CountryFlag countryCode={pickCountryRaw(data)} />
              </span>
              {data.online ? (
                <span className="admin-user-details__chip admin-user-details__chip--online admin-user-details__chip--with-icon">
                  <CircleCheck size={15} strokeWidth={2} className="admin-user-details__chip-icon" aria-hidden />
                  {t("admin.status.online", { defaultValue: "Online" })}
                </span>
              ) : (
                <span className="admin-user-details__chip admin-user-details__chip--offline admin-user-details__chip--with-icon">
                  <CircleX size={15} strokeWidth={2} className="admin-user-details__chip-icon" aria-hidden />
                  {t("admin.status.offline", { defaultValue: "Offline" })}
                </span>
              )}
              <span className="admin-user-details__chip admin-user-details__chip--verify admin-user-details__chip--with-icon">
                <ShieldCheck size={15} strokeWidth={2} className="admin-user-details__chip-icon" aria-hidden />
                {String(data.verificationStatus ?? "—")}
              </span>
            </div>
          </div>
        </div>
        <aside className="admin-user-details__hero-aside" aria-label={t("admin.users.account_meta", { defaultValue: "Account meta" })}>
          <div className="admin-user-details__meta-grid">
            <div className="admin-user-details__meta-cell">
              <span className="admin-user-details__meta-label">{t("admin.users.user_id", { defaultValue: "User ID" })}</span>
              <span className="admin-user-details__meta-value mono">
                {uid}
              </span>
            </div>
            <div className="admin-user-details__meta-cell">
              <span className="admin-user-details__meta-label">{t("admin.users.member_since", { defaultValue: "Member since" })}</span>
              <span className="admin-user-details__meta-value">
                {formatMaybeTimestamp(data.createdAt)}
              </span>
            </div>
            <div className="admin-user-details__meta-cell">
              <span className="admin-user-details__meta-label">{t("admin.users.last_active", { defaultValue: "Last active" })}</span>
              <span className="admin-user-details__meta-value">
                {formatLastActive(data)}
              </span>
            </div>
            <div className="admin-user-details__meta-cell">
              <span className="admin-user-details__meta-label">
                {t("admin.verification.status", { defaultValue: "Verification status" })}
              </span>
              <span className="admin-user-details__meta-value">
                {String(data.verificationStatus ?? "—")}
              </span>
            </div>
          </div>
        </aside>
      </section>

      <div className="admin-user-details__grid">
        <CardShell index={1} title={t("admin.users.profile", { defaultValue: "Profile" })} iconVariant="profile" Icon={User}>
          <DetailRow label="displayName / name">{displayName}</DetailRow>
          <DetailRow label="gender">{String(data.gender ?? "—")}</DetailRow>
          <DetailRow label="country">
            <CountryFlag countryCode={pickCountryRaw(data)} />
          </DetailRow>
          <DetailRow label="language">{langStr || "—"}</DetailRow>
          <DetailRow label="email">{emailStr || "—"}</DetailRow>
          <DetailRow label="phone">{phoneStr || "—"}</DetailRow>
          <DetailRow label="online">{data.online ? "Online" : "Offline"}</DetailRow>
          <DetailRow label="verificationStatus">
            {String(data.verificationStatus ?? "—")}
          </DetailRow>
          <DetailRow label="role">
            {Array.isArray(data.role)
              ? data.role.join(", ") || "—"
              : String(data.role ?? "—")}
          </DetailRow>
        </CardShell>

        <CardShell index={2} title={t("admin.finance.wallet", { defaultValue: "Wallet" })} iconVariant="wallet" Icon={Wallet}>
          <DetailRow label="coins">
            {nf.format(toNumber(data.coins ?? data.coinsBalance, 0))}
          </DetailRow>
          <DetailRow label="earnings.availableTokens">
            {nf.format(toNumber(data?.earnings?.availableTokens, 0))}
          </DetailRow>
          <DetailRow label="earnings.lifetimeTokens">
            {nf.format(toNumber(data?.earnings?.lifetimeTokens, 0))}
          </DetailRow>
          <DetailRow label="femaleLevel">
            {data.femaleLevel != null && String(data.femaleLevel).trim() !== ""
              ? String(data.femaleLevel)
              : "—"}
          </DetailRow>
          <DetailRow label="leveling.tokens">
            {data?.leveling?.tokens != null
              ? nf.format(toNumber(data.leveling.tokens, 0))
              : "—"}
          </DetailRow>
          <DetailRow label="leveling.activeDays">
            {data?.leveling?.activeDays != null
              ? String(data.leveling.activeDays)
              : "—"}
          </DetailRow>
        </CardShell>

        <CardShell index={3} title={t("admin.withdrawals.withdrawal", { defaultValue: "Withdrawal" })} iconVariant="withdrawal" Icon={BadgeDollarSign}>
          <DetailRow label="withdrawEligibility.status">
            {String(data?.withdrawEligibility?.status ?? "—")}
          </DetailRow>
          <DetailRow label="withdrawEligibility.approvedAt">
            {formatMaybeTimestamp(data?.withdrawEligibility?.approvedAt)}
          </DetailRow>
          <DetailRow label="withdrawEligibility.rejectedAt">
            {formatMaybeTimestamp(data?.withdrawEligibility?.rejectedAt)}
          </DetailRow>
          <DetailRow label="withdrawEligibility.rejectionReason">
            <span className="admin-user-details__multiline">
              {data?.withdrawEligibility?.rejectionReason != null &&
              String(data.withdrawEligibility.rejectionReason).trim() !== ""
                ? String(data.withdrawEligibility.rejectionReason)
                : "—"}
            </span>
          </DetailRow>
          <DetailRow label="withdrawControl.nextAllowedWithdrawAtMs">
            {formatMs(data?.withdrawControl?.nextAllowedWithdrawAtMs)}
          </DetailRow>
          <DetailRow label="withdrawControl.lastWithdrawPaidAtMs">
            {formatMs(data?.withdrawControl?.lastWithdrawPaidAtMs)}
          </DetailRow>
        </CardShell>

        <CardShell index={4} title={t("admin.users.premium", { defaultValue: "Premium" })} iconVariant="premium" Icon={Crown}>
          <DetailRow label="premium.active">
            {String(data?.premium?.active ?? "—")}
          </DetailRow>
          <DetailRow label="premium.expiresAt">
            {formatMaybeTimestamp(data?.premium?.expiresAt)}
          </DetailRow>
          <DetailRow label="premium.country">
            <CountryFlag
              countryCode={
                data?.premium?.country != null &&
                String(data.premium.country).trim() !== ""
                  ? String(data.premium.country).trim()
                  : ""
              }
            />
          </DetailRow>
          <DetailRow label="premium.source">
            {data?.premium?.source != null &&
            String(data.premium.source).trim() !== ""
              ? String(data.premium.source)
              : "—"}
          </DetailRow>
        </CardShell>

        <CardShell index={5} title={t("admin.users.safety_status", { defaultValue: "Safety & Status" })} iconVariant="safety" Icon={ShieldCheck}>
          <DetailRow label="admin">
            {data.admin === true ? (
              <span className="admin-user-details__pill-warn">true</span>
            ) : (
              String(data.admin ?? "—")
            )}
          </DetailRow>
          <DetailRow label="isAdmin">
            {data.isAdmin === true ? (
              <span className="admin-user-details__pill-warn">true</span>
            ) : (
              String(data.isAdmin ?? "—")
            )}
          </DetailRow>
          <DetailRow label="accountStatus">
            {String(data.accountStatus ?? "—")}
          </DetailRow>
          {isSuspended ? (
            <>
              <DetailRow label="suspensionReason">
                <span className="admin-user-details__multiline">
                  {data?.suspensionReason != null &&
                  String(data.suspensionReason).trim() !== ""
                    ? String(data.suspensionReason)
                    : "—"}
                </span>
              </DetailRow>
              <DetailRow label="suspendedAt">
                {formatMaybeTimestamp(data?.suspendedAt)}
              </DetailRow>
              <DetailRow label="suspendedBy">
                <span className="admin-user-details__mono-wrap">
                  {String(data.suspendedBy ?? "—")}
                </span>
              </DetailRow>
            </>
          ) : null}
          <DetailRow label={t("admin.users.blocked_by_you", { defaultValue: "Blocked by you" })}>
            {blockedByMe ? (
              <span className="admin-user-details__pill-danger">{t("admin.users.blocked", { defaultValue: "Blocked" })}</span>
            ) : (
              <span className="muted">{t("admin.users.not_blocked", { defaultValue: "Not blocked" })}</span>
            )}
          </DetailRow>
        </CardShell>

        <article className="admin-user-details__card admin-user-details__card--quick">
          <div className="admin-user-details__card-head">
            <span
              className="admin-user-details__card-icon admin-user-details__card-icon--actions"
              aria-hidden
            >
              <Zap
                className="admin-user-details__card-icon-svg"
                size={18}
                strokeWidth={2}
              />
            </span>
            <h3 className="admin-user-details__card-title">6. Quick Actions</h3>
          </div>
          <div className="admin-user-details__quick-actions">
            {canSuspend ? (
              isSuspended ? (
                <button
                  type="button"
                  className="admin-user-details__action-row admin-user-details__action-row--unban"
                  disabled={suspendWorking}
                  onClick={() => void handleUnsuspend()}
                >
                  <span className="admin-user-details__action-row__start">
                    <CircleCheck
                      className="admin-user-details__action-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="admin-user-details__action-label">
                      {suspendWorking
                        ? "…"
                        : t("admin.users.unsuspend", { defaultValue: "Unsuspend" })}
                    </span>
                  </span>
                  <span className="admin-user-details__action-chevron">→</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="admin-user-details__action-row admin-user-details__action-row--ban"
                  disabled={suspendWorking}
                  onClick={() => openSuspendConfirm()}
                >
                  <span className="admin-user-details__action-row__start">
                    <Ban
                      className="admin-user-details__action-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="admin-user-details__action-label">
                      {suspendWorking
                        ? "…"
                        : t("admin.users.suspend_account", {
                            defaultValue: "Suspend account",
                          })}
                    </span>
                  </span>
                  <span className="admin-user-details__action-chevron">→</span>
                </button>
              )
            ) : null}
            {canForceOffline ? (
              <button
                type="button"
                className="admin-user-details__action-row admin-user-details__action-row--force-offline"
                disabled={forceOfflineWorking}
                onClick={() => openForceOfflineConfirm()}
              >
                <span className="admin-user-details__action-row__start">
                  <WifiOff
                    className="admin-user-details__action-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  <span className="admin-user-details__action-label">
                    {forceOfflineWorking
                      ? "…"
                      : t("admin.users.force_offline", {
                          defaultValue: "Force offline",
                        })}
                  </span>
                </span>
                {/* Correction V2 #1: this decorative chevron must not be
                    part of the button's accessible name ("Force offline",
                    not "Force offline →") - the other, pre-existing Quick
                    Actions rows are intentionally left untouched. */}
                <span className="admin-user-details__action-chevron" aria-hidden="true">
                  →
                </span>
              </button>
            ) : null}
            {canAllowOnline ? (
              <button
                type="button"
                className="admin-user-details__action-row admin-user-details__action-row--allow-online"
                disabled={allowOnlineWorking}
                onClick={() => openAllowOnlineConfirm()}
              >
                <span className="admin-user-details__action-row__start">
                  <Wifi
                    className="admin-user-details__action-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  <span className="admin-user-details__action-label">
                    {allowOnlineWorking
                      ? "…"
                      : t("admin.users.allow_online", {
                          defaultValue: "Allow online again",
                        })}
                  </span>
                </span>
                {/* Same convention as Force offline's row above - the
                    decorative chevron is excluded from the accessible
                    name. */}
                <span className="admin-user-details__action-chevron" aria-hidden="true">
                  →
                </span>
              </button>
            ) : null}
            <Link
              className="admin-user-details__action-row admin-user-details__action-row--support"
              to="/admin1/support"
            >
              <span className="admin-user-details__action-row__start">
                <MessageSquare
                  className="admin-user-details__action-icon"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
                <span className="admin-user-details__action-label">{t("admin.support.title", { defaultValue: "Support" })}</span>
              </span>
              <span className="admin-user-details__action-chevron">→</span>
            </Link>
            <Link
              className="admin-user-details__action-row admin-user-details__action-row--reports"
              to="/admin1/reports"
            >
              <span className="admin-user-details__action-row__start">
                <Flag
                  className="admin-user-details__action-icon"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
                <span className="admin-user-details__action-label">{t("admin.reports.title", { defaultValue: "Reports" })}</span>
              </span>
              <span className="admin-user-details__action-chevron">→</span>
            </Link>
            <Link
              className="admin-user-details__action-row admin-user-details__action-row--transactions"
              to="/admin1/finance"
            >
              <span className="admin-user-details__action-row__start">
                <ListChecks
                  className="admin-user-details__action-icon"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
                <span className="admin-user-details__action-label">{t("admin.finance.transactions", { defaultValue: "Transactions" })}</span>
              </span>
              <span className="admin-user-details__action-chevron">→</span>
            </Link>
            <Link
              className="admin-user-details__action-row admin-user-details__action-row--logs"
              to="/admin1/accounting-reports"
            >
              <span className="admin-user-details__action-row__start">
                <Clock
                  className="admin-user-details__action-icon"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
                <span className="admin-user-details__action-label">{t("admin.audit_logs.logs", { defaultValue: "Logs" })}</span>
              </span>
              <span className="admin-user-details__action-chevron">→</span>
            </Link>
          </div>
        </article>
      </div>

      <section
        className="admin-user-details__grant-coins"
        aria-labelledby="admin-user-details-grant-coins-title"
      >
        <div className="admin-user-details__card admin-user-details__card--wide">
          <div className="admin-user-details__card-head">
            <span
              className="admin-user-details__card-icon admin-user-details__card-icon--wallet"
              aria-hidden
            >
              <Coins
                className="admin-user-details__card-icon-svg"
                size={18}
                strokeWidth={2}
              />
            </span>
            <h3
              className="admin-user-details__card-title"
              id="admin-user-details-grant-coins-title"
            >
              {t("admin.users.grant_coins_title", { defaultValue: "Grant coins" })}
            </h3>
          </div>
          <div className="admin-user-details__card-body admin-user-details__grant-coins-body">
            <p className="admin-user-details__grant-coins-hint">
              {t("admin.users.grant_coins_hint", {
                defaultValue:
                  "Adds coins to this user's balance via the finance-admin-only adminGrantCoins function. Every grant is idempotent and recorded in the audit log.",
              })}
            </p>
            <div className="admin-user-details__grant-coins-row">
              <div className="admin-user-details__grant-coins-field">
                <label
                  className="admin-user-details__grant-coins-label"
                  htmlFor="admin-grant-coins-amount"
                >
                  {t("admin.users.grant_coins_amount_label", { defaultValue: "Amount" })}
                </label>
                <select
                  id="admin-grant-coins-amount"
                  className="admin-user-details__grant-coins-select"
                  value={grantAmount}
                  disabled={grantWorking}
                  onChange={(e) => setGrantAmount(e.target.value)}
                >
                  <option value="">
                    {t("admin.users.grant_coins_amount_placeholder", {
                      defaultValue: "Select amount",
                    })}
                  </option>
                  {GRANT_COIN_AMOUNTS.map((amount) => (
                    <option key={amount} value={amount}>
                      {nf.format(amount)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-user-details__grant-coins-field admin-user-details__grant-coins-field--reason">
                <label
                  className="admin-user-details__grant-coins-label"
                  htmlFor="admin-grant-coins-reason"
                >
                  {t("admin.users.grant_coins_reason_label", {
                    defaultValue: "Reason (optional)",
                  })}
                </label>
                <textarea
                  id="admin-grant-coins-reason"
                  className="admin-user-details__grant-coins-textarea"
                  rows={2}
                  maxLength={GRANT_REASON_MAX}
                  value={grantReason}
                  disabled={grantWorking}
                  onChange={(e) =>
                    setGrantReason(e.target.value.slice(0, GRANT_REASON_MAX))
                  }
                  placeholder={t("admin.users.grant_coins_reason_placeholder", {
                    defaultValue: "Add context for the audit log…",
                  })}
                  autoComplete="off"
                />
                <div className="admin-user-details__grant-coins-counter" aria-live="polite">
                  {grantReason.length} / {GRANT_REASON_MAX}
                </div>
              </div>
              <div className="admin-user-details__grant-coins-field admin-user-details__grant-coins-field--submit">
                <button
                  type="button"
                  className="admin-user-details__btn admin-user-details__btn--success admin-user-details__btn--with-icon"
                  disabled={grantWorking || !grantAmount}
                  onClick={() => openGrantCoinsConfirm()}
                >
                  <Coins size={16} strokeWidth={2} aria-hidden />
                  <span>
                    {grantWorking
                      ? "…"
                      : t("admin.users.grant_coins_cta", { defaultValue: "Grant coins" })}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="admin-user-details__activity">
        <div className="admin-user-details__card admin-user-details__card--wide">
          <div className="admin-user-details__card-head">
            <span
              className="admin-user-details__card-icon admin-user-details__card-icon--activity"
              aria-hidden
            >
              <Activity
                className="admin-user-details__card-icon-svg"
                size={18}
                strokeWidth={2}
              />
            </span>
            <h3 className="admin-user-details__card-title">
              Activity overview
            </h3>
          </div>
          <div className="admin-user-details__activity-stats">
            <div className="admin-user-details__mini-stat">
              <span className="admin-user-details__mini-label">
                Last 7 days earnings
              </span>
              <span className="admin-user-details__mini-value">
                {activityOverview.earnings7d}
              </span>
            </div>
            <div className="admin-user-details__mini-stat">
              <span className="admin-user-details__mini-label">
                Calls this week
              </span>
              <span className="admin-user-details__mini-value">
                {activityOverview.callsWeek}
              </span>
            </div>
            <div className="admin-user-details__mini-stat">
              <span className="admin-user-details__mini-label">
                Messages this week
              </span>
              <span className="admin-user-details__mini-value">
                {activityOverview.messagesWeek}
              </span>
            </div>
            <div className="admin-user-details__mini-stat">
              <span className="admin-user-details__mini-label">
                Online time (7 days)
              </span>
              <span className="admin-user-details__mini-value">
                {activityOverview.onlineTime7d}
              </span>
            </div>
            <div className="admin-user-details__mini-stat">
              <span className="admin-user-details__mini-label">
                Login count (7 days)
              </span>
              <span className="admin-user-details__mini-value">
                {activityOverview.logins7d}
              </span>
            </div>
          </div>
        </div>
      </section>

      <DevicesUsedSection
        devices={devices}
        loading={devicesLoading}
        error={devicesError}
      />

      <OpsConsoleControls
        loading={opsLoading}
        error={opsError}
        generatedAt={opsSnapshot?.generatedAt}
        onRefresh={() => fetchOpsSnapshot(uid)}
      />

      <div className="admin-user-details__grid">
        <AuthIdentitySection snapshot={opsSnapshot} loading={opsLoading} error={opsError} />
        <PresenceDiagnosticsSection snapshot={opsSnapshot} loading={opsLoading} error={opsError} />
        <ActiveCallSection snapshot={opsSnapshot} loading={opsLoading} error={opsError} uid={uid} />
      </div>

      <RecentCallsSection snapshot={opsSnapshot} loading={opsLoading} error={opsError} uid={uid} />

      <div className="admin-user-details__grid">
        <ProgressionSection snapshot={opsSnapshot} loading={opsLoading} error={opsError} />
      </div>

      <details className="admin-user-details__raw">
        <summary className="admin-user-details__raw-summary">
          <span className="admin-user-details__raw-summary-inner">
            <span
              className="admin-user-details__card-icon admin-user-details__card-icon--raw"
              aria-hidden
            >
              <Code2
                className="admin-user-details__card-icon-svg"
                size={18}
                strokeWidth={2}
              />
            </span>
            <span className="admin-user-details__raw-summary-label">
              Raw user document (JSON)
            </span>
          </span>
        </summary>
        <pre className="admin-user-details__raw-pre">{fullJson}</pre>
      </details>
    </>
  ) : null;

  return (
    <div dir="ltr" lang="en" className="admin-user-details admin1-dashboard">
      {inner}
      {data && suspendConfirmOpen ? (
        <AdminSuspendConfirmModal
          targetLine={`${displayName} · ${uid}`}
          reason={suspendReasonDraft}
          onReasonChange={setSuspendReasonDraft}
          onClose={() => {
            if (suspendWorking) return;
            setSuspendConfirmOpen(false);
          }}
          onConfirm={confirmSuspendFromModal}
          working={suspendWorking}
          t={t}
        />
      ) : null}
      {data && grantConfirmOpen ? (
        <AdminGrantCoinsConfirmModal
          targetLine={`${displayName} · ${uid}`}
          amountLabel={`${t("admin.users.grant_coins_amount_label", {
            defaultValue: "Amount",
          })}: ${nf.format(Number(grantAmount) || 0)} ${t("admin.users.grant_coins_unit", {
            defaultValue: "coins",
          })}`}
          reason={grantReason.trim()}
          onClose={closeGrantCoinsConfirm}
          onConfirm={confirmGrantCoins}
          working={grantWorking}
          t={t}
        />
      ) : null}
      {data && forceOfflineConfirmOpen ? (
        <AdminForceOfflineConfirmModal
          targetLine={`${displayName} · ${uid}`}
          reason={forceOfflineReasonDraft}
          onReasonChange={(value) => {
            setForceOfflineReasonDraft(value);
            // Correction V3 #1: editing the reason clears any stale
            // error (validation or a prior operation failure) without
            // invoking the callable - the admin is actively correcting
            // their input, so the old message is no longer relevant.
            if (forceOfflineValidationError) setForceOfflineValidationError("");
            if (forceOfflineOperationError) setForceOfflineOperationError("");
          }}
          validationError={forceOfflineValidationError}
          operationError={forceOfflineOperationError}
          onClose={closeForceOfflineConfirm}
          onConfirm={confirmForceOffline}
          working={forceOfflineWorking}
          t={t}
        />
      ) : null}
      {data && allowOnlineConfirmOpen ? (
        <AdminAllowOnlineConfirmModal
          targetLine={`${displayName} · ${uid}`}
          reason={allowOnlineReasonDraft}
          onReasonChange={(value) => {
            setAllowOnlineReasonDraft(value);
            // Same convention as Force offline's dialog above: editing
            // the reason clears any stale error (validation or a prior
            // operation failure) without invoking the callable.
            if (allowOnlineValidationError) setAllowOnlineValidationError("");
            if (allowOnlineOperationError) setAllowOnlineOperationError("");
          }}
          validationError={allowOnlineValidationError}
          operationError={allowOnlineOperationError}
          onClose={closeAllowOnlineConfirm}
          onConfirm={confirmAllowOnline}
          working={allowOnlineWorking}
          t={t}
        />
      ) : null}
    </div>
  );
}
